import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { useAuth } from '@/app/providers/authContext';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query';

// Type-only, and reached by its own file rather than the `@/features/orders` barrel: an
// `import type` is erased by `verbatimModuleSyntax` and leaves no runtime edge, but importing the
// barrel would still pull in `OrdersRoute.tsx` and everything it imports at evaluation time —
// `Order` is the only thing this module needs to know about the other feature.
import type { Order } from '@/features/orders/contracts';

import type { Cart, CheckoutQuote, PlaceOrderRequest, UpsertCartItemRequest } from './contracts';

const CART = '/v1/orders/cart';

interface TenantBranch {
  readonly key: string;
  readonly enabled: boolean;
}

/**
 * No request carries the tenant: the API derives it from the `tenant_id` claim and Row Level
 * Security filters the rows, the same rule every other feature module in this SPA follows. The
 * claim still opens every cache key so a second sign-in on the same browser cannot read the first
 * tenant's cached cart.
 */
function useTenantBranch(): TenantBranch {
  const { tenantId } = useAuth();
  return { key: tenantId ?? 'sem-tenant', enabled: tenantId !== null };
}

/**
 * The caller's open cart, priced. `establishmentId` is required by the route — a cart belongs to
 * one (tenant, establishment, user) — so the query stays disabled until it is known, which is what
 * lets `useCurrentEstablishment` and this hook compose without a screen having to juggle a
 * placeholder id.
 */
export function useCartQuery(establishmentId: string | undefined): UseQueryResult<Cart, Error> {
  const { key, enabled } = useTenantBranch();

  return useQuery({
    queryKey: [...queryKeys.cart(key, establishmentId ?? 'sem-estabelecimento'), 'cart'],
    queryFn: ({ signal }) =>
      api.get<Cart>(CART, { query: { establishment_id: establishmentId }, signal }),
    enabled: enabled && establishmentId !== undefined,
    // The cart is what "survives a change of device" (RNF08's acceptance line): a second tab or a
    // second device must see the same lines soon after they change, so it is worth the extra
    // request a shorter fresh window costs.
    staleTime: 15_000,
  });
}

/** The server's current subtotal, unit-configured freight/coupon and delivery estimate. */
export function useCheckoutQuoteQuery(
  establishmentId: string | undefined,
  couponCode: string | undefined,
): UseQueryResult<CheckoutQuote, Error> {
  const { key, enabled } = useTenantBranch();

  return useQuery({
    queryKey: [
      ...queryKeys.cart(key, establishmentId ?? 'sem-estabelecimento'),
      'checkout-quote',
      couponCode ?? '',
    ],
    queryFn: ({ signal }) =>
      api.get<CheckoutQuote>('/v1/orders/cart/checkout-quote', {
        query: {
          establishment_id: establishmentId,
          ...(couponCode ? { coupon_code: couponCode } : {}),
        },
        signal,
      }),
    enabled: enabled && establishmentId !== undefined,
    staleTime: 10_000,
  });
}

/**
 * Drops the cached cart for one establishment after a write to it. Narrower than invalidating the
 * whole `cart` branch across establishments: a write to one establishment's cart says nothing
 * about whether another establishment's cart is still fresh.
 */
export function useCartInvalidation(establishmentId: string | undefined): () => Promise<void> {
  const queryClient = useQueryClient();
  const { key } = useTenantBranch();

  return async () => {
    if (establishmentId === undefined) {
      return;
    }
    await queryClient.invalidateQueries({ queryKey: queryKeys.cart(key, establishmentId) });
  };
}

export interface UpsertCartItemInput {
  readonly skuId: string;
  readonly establishmentId: string;
  readonly quantity: number;
}

/**
 * Adds a line or changes its quantity — there is no separate "add to cart" verb, the route is an
 * upsert keyed by SKU. This is what backs both the catalogue's "Adicionar ao carrinho" control and
 * the quantity stepper on the cart screen itself: the two are the same request with a different
 * starting quantity.
 */
export function useUpsertCartItemMutation(
  establishmentId: string | undefined,
): UseMutationResult<Cart, Error, UpsertCartItemInput> {
  const invalidate = useCartInvalidation(establishmentId);

  return useMutation({
    mutationFn: ({ skuId, establishmentId: forEstablishment, quantity }: UpsertCartItemInput) =>
      api.put<Cart>(`${CART}/items/${encodeURIComponent(skuId)}`, {
        establishment_id: forEstablishment,
        quantity,
      } satisfies UpsertCartItemRequest),
    onSuccess: invalidate,
  });
}

export interface RemoveCartItemInput {
  readonly skuId: string;
  readonly establishmentId: string;
}

export function useRemoveCartItemMutation(
  establishmentId: string | undefined,
): UseMutationResult<Cart, Error, RemoveCartItemInput> {
  const invalidate = useCartInvalidation(establishmentId);

  return useMutation({
    mutationFn: ({ skuId, establishmentId: forEstablishment }: RemoveCartItemInput) =>
      api.delete<Cart>(`${CART}/items/${encodeURIComponent(skuId)}`, {
        query: { establishment_id: forEstablishment },
      }),
    onSuccess: invalidate,
  });
}

/** Empties every line at once — the "esvaziar carrinho" action, distinct from removing one line. */
export function useEmptyCartMutation(
  establishmentId: string | undefined,
): UseMutationResult<Cart, Error, { establishmentId: string }> {
  const invalidate = useCartInvalidation(establishmentId);

  return useMutation({
    mutationFn: ({ establishmentId: forEstablishment }: { establishmentId: string }) =>
      api.delete<Cart>(CART, { query: { establishment_id: forEstablishment } }),
    onSuccess: invalidate,
  });
}

/**
 * Places the order from the caller's open cart. Lives here rather than in `features/orders`
 * because what it invalidates is the cart, not an order list: placing empties the open cart (its
 * status moves to `ordered`) and a fresh one takes its place, so the screen the buyer is
 * confirming from must stop showing the lines that were just turned into an order.
 */
export function usePlaceOrderMutation(
  establishmentId: string | undefined,
): UseMutationResult<Order, Error, PlaceOrderRequest> {
  const queryClient = useQueryClient();
  const { key } = useTenantBranch();
  const invalidateCart = useCartInvalidation(establishmentId);

  return useMutation({
    mutationFn: (values: PlaceOrderRequest) => api.post<Order>('/v1/orders', values),
    onSuccess: async () => {
      await invalidateCart();
      if (establishmentId !== undefined) {
        await queryClient.invalidateQueries({ queryKey: queryKeys.orders(key, establishmentId) });
      }
    },
  });
}
